/**
 * Login page.
 *
 * Clean login form with username, password, and "Remember me" checkbox.
 * Submits credentials to POST /api/auth/login via the useAuth hook.
 * On success, redirects to the originally intended URL (or / by default).
 * Handles 401 (invalid credentials) and 429 (rate-limited) errors with
 * distinct user-facing messages.
 *
 * Spec reference: §6.7 (Frontend: Login Page).
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const usernameRef = useRef(null);

  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Post-login redirect target (set by ProtectedRoute)
  const from = location.state?.from?.pathname || '/';

  // Auto-focus the username field on mount
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  // Show a centered spinner while the initial auth check runs
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-400 text-sm">Loading&hellip;</div>
      </div>
    );
  }

  // Already logged in — skip straight to intended page
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  /**
   * Map an Axios error to a user-facing message.
   *
   * @param {import('axios').AxiosError} err
   * @returns {string}
   */
  function errorMessage(err) {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;

    if (status === 429) {
      return 'Too many login attempts. Please wait a minute and try again.';
    }
    if (status === 401) {
      return detail || 'Invalid username or password.';
    }
    if (!err.response) {
      return 'Unable to reach the server. Please check your connection.';
    }
    return detail || 'An unexpected error occurred. Please try again.';
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(username, password, rememberMe);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="mx-auto mb-3 flex h-12 w-12 items-center justify-center
                          rounded-full bg-blue-100"
            >
              {/* Bar-chart icon (Heroicons outline) */}
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0
                     1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21
                     6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75
                     8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0
                     1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125
                     1.125h-2.25a1.125 1.125 0 0
                     1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125
                     1.125-1.125h2.25C20.496 3 21 3.504 21
                     4.125v15.75c0 .621-.504 1.125-1.125
                     1.125h-2.25a1.125 1.125 0 0
                     1-1.125-1.125V4.125Z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              Price Monitoring
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Sign in to your account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error banner */}
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md bg-red-50
                           border border-red-200 p-3 text-sm text-red-700"
              >
                {/* Exclamation-circle icon */}
                <svg
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18
                       0Zm-9 3.75h.008v.008H12v-.008Z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Username
              </label>
              <input
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300
                           shadow-sm focus:border-blue-500 focus:ring-blue-500
                           disabled:bg-gray-50 disabled:text-gray-400
                           sm:text-sm"
                autoComplete="username"
                placeholder="Enter your username"
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300
                           shadow-sm focus:border-blue-500 focus:ring-blue-500
                           disabled:bg-gray-50 disabled:text-gray-400
                           sm:text-sm"
                autoComplete="current-password"
                placeholder="Enter your password"
              />
            </div>

            {/* Remember me */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  name="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={submitting}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600
                             focus:ring-blue-500 disabled:opacity-50"
                />
                <label
                  htmlFor="remember"
                  className="ml-2 text-sm text-gray-600"
                >
                  Remember me
                </label>
              </div>
              <span className="text-xs text-gray-400">
                {rememberMe ? '90-day session' : '30-day session'}
              </span>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2
                         rounded-md bg-blue-600 px-4 py-2.5 text-sm
                         font-medium text-white shadow-sm
                         hover:bg-blue-700 focus:outline-none focus:ring-2
                         focus:ring-blue-500 focus:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
            >
              {submitting && (
                <svg
                  className="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0
                       12h4zm2 5.291A7.962 7.962 0 014 12H0c0
                       3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {submitting ? 'Signing in\u2026' : 'Sign in'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-gray-400">
          All prices in GBP (&pound;)
        </p>
      </div>
    </div>
  );
}
